import { useState } from 'react';
import { Activity, Database, HardDrive, ShieldCheck, Boxes } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatPill } from '@/components/charts/StatPill';
import { ThroughputChart } from '@/components/charts/ThroughputChart';
import { StorageGrowthChart } from '@/components/charts/StorageGrowthChart';
import { BlockedWorkers } from '@/components/charts/BlockedWorkers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, stateVariant } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/data/EmptyState';
import { TimeframeSelect } from '@/components/ui/TimeframeSelect';
import { DEFAULT_TIMEFRAME, getTimeframe, type TimeframeKey } from '@/config/timeframes';
import {
  useManifestSummary,
  useManifestThroughput,
} from '@/hooks/useManifest';
import {
  JOB_STATE_COUNTS_ROW_CAP,
  useJobStateCounts,
  useHangfireServers,
} from '@/hooks/useHangfire';
import { useStorageGrowth } from '@/hooks/useStorageGrowth';
import { useRecentAdminActions } from '@/hooks/useCronJobs';
import { formatBytes, formatNumber, formatRelative } from '@/lib/utils';
import { PROVIDERS } from '@/config/providers';

// All-time has no lower bound; useJobStateCounts needs a number, so pass a
// decade of hours to approximate "all".
const ALL_TIME_HOURS = 24 * 365 * 10;

export function DashboardRoute() {
  const [timeframe, setTimeframe] = useState<TimeframeKey>(DEFAULT_TIMEFRAME);
  const tf = getTimeframe(timeframe);
  const windowHours = tf.hours ?? ALL_TIME_HOURS;

  const summary = useManifestSummary();
  const throughput = useManifestThroughput(timeframe);
  const states = useJobStateCounts(windowHours);
  const servers = useHangfireServers();
  const storage = useStorageGrowth(timeframe);
  const actions = useRecentAdminActions(8);

  const totals = (summary.data ?? []).reduce(
    (acc, r) => {
      acc.objects += Number(r.object_count) || 0;
      acc.bytes += Number(r.bytes_total) || 0;
      return acc;
    },
    { objects: 0, bytes: 0 },
  );

  const lastCollected = (summary.data ?? [])
    .map((r) => r.last_collected_at)
    .filter((x): x is string => !!x)
    .sort()
    .pop();

  // % growth = bytes added in the window relative to the pre-window baseline
  // (all-time total minus what landed in the window). Null when there's no
  // baseline yet (everything is brand new) or storage hasn't loaded.
  const baselineBytes = Math.max(totals.bytes - (storage.data?.bytesInWindow ?? 0), 0);
  const growthPct =
    storage.data && baselineBytes > 0 ? (storage.data.bytesInWindow / baselineBytes) * 100 : null;

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title="Overview"
          description="A live snapshot of harvest activity, Hangfire health, and storage growth across all providers."
        />
        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs text-muted-foreground">Window</span>
          <TimeframeSelect value={timeframe} onChange={setTimeframe} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatPill
          label="Manifest objects"
          // Show "—" on isError too — a failed query also produces undefined
          // summary.data, so totals would otherwise show an authoritative-
          // looking "0 objects". The Coverage-by-provider card below
          // surfaces the actual error message.
          value={summary.isLoading || summary.isError ? '—' : formatNumber(totals.objects)}
          icon={Database}
        />
        <StatPill
          label="Storage size"
          value={summary.isLoading || summary.isError ? '—' : formatBytes(totals.bytes)}
          icon={HardDrive}
        />
        <StatPill
          label="Live Hangfire servers"
          value={servers.isLoading || servers.isError ? '—' : (servers.data?.length ?? 0)}
          icon={Activity}
          hint={servers.data?.[0]?.lastheartbeat ? `Last beat ${formatRelative(servers.data[0].lastheartbeat)}` : undefined}
        />
        <StatPill
          label="Last collection"
          value={lastCollected ? formatRelative(lastCollected) : '—'}
          icon={ShieldCheck}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Manifest throughput · {tf.label} (UTC)</CardTitle>
            <CardDescription>S3 object writes registered in harvest.manifest, bucketed over the selected window.</CardDescription>
          </CardHeader>
          <CardContent>
            {throughput.isError ? (
              <ErrorState error={throughput.error} />
            ) : throughput.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <ThroughputChart data={throughput.data?.buckets ?? []} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Job state mix · {tf.label}</CardTitle>
            <CardDescription>Hangfire job outcomes from harvest.job.</CardDescription>
          </CardHeader>
          <CardContent>
            {states.isError ? (
              <ErrorState error={states.error} />
            ) : states.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <>
                {states.data?.truncated && (
                  <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground">
                    State counts hit the {formatNumber(JOB_STATE_COUNTS_ROW_CAP)}-row
                    scan cap — totals may be under-counted.
                  </div>
                )}
                <ul className="space-y-2">
                  {states.data?.counts.length ? (
                    states.data.counts.map((s) => (
                      <li key={s.state} className="flex items-center justify-between text-sm">
                        <Badge variant={stateVariant(s.state)}>{s.state}</Badge>
                        <span className="tabular-nums">{formatNumber(s.count)}</span>
                      </li>
                    ))
                  ) : (
                    <div className="text-xs text-muted-foreground">No jobs in window.</div>
                  )}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Storage growth · {tf.label} (UTC)</CardTitle>
            <CardDescription>Bytes written to S3 (harvest.manifest), bucketed over the selected window.</CardDescription>
          </CardHeader>
          <CardContent>
            {storage.isError ? (
              <ErrorState error={storage.error} />
            ) : storage.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <>
                <div className="mb-3 flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-muted-foreground">Added</span>{' '}
                    <span className="font-medium tabular-nums">{formatBytes(storage.data?.bytesInWindow ?? 0)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Growth</span>{' '}
                    <span className="font-medium tabular-nums">
                      {growthPct == null ? '—' : `+${growthPct.toFixed(growthPct < 10 ? 1 : 0)}%`}
                    </span>
                  </div>
                </div>
                <StorageGrowthChart data={storage.data?.buckets ?? []} />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Blocked workers</CardTitle>
            <CardDescription>Workers held by in-flight jobs, and how long each has been running.</CardDescription>
          </CardHeader>
          <CardContent>
            <BlockedWorkers />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Coverage by provider</CardTitle>
            <CardDescription>Manifest object counts grouped by provider + sport.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {summary.isError ? (
              <ErrorState error={summary.error} />
            ) : summary.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              PROVIDERS.map((p) => {
                const rows = (summary.data ?? []).filter((r) => r.provider === p.key);
                const sportTotals = new Map<string, { objects: number; bytes: number }>();
                for (const r of rows) {
                  const prev = sportTotals.get(r.sport) ?? { objects: 0, bytes: 0 };
                  prev.objects += Number(r.object_count) || 0;
                  prev.bytes += Number(r.bytes_total) || 0;
                  sportTotals.set(r.sport, prev);
                }
                return (
                  <div key={p.key} className="rounded-md border border-border p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Boxes className="h-4 w-4 text-primary" />
                        <Link to={`/entities/${p.key}`} className="text-sm font-medium hover:underline">
                          {p.label}
                        </Link>
                      </div>
                      <span className="text-xs text-muted-foreground">{p.hangfireQueue}</span>
                    </div>
                    {sportTotals.size === 0 ? (
                      <div className="text-xs text-muted-foreground">No manifest rows yet.</div>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {[...sportTotals.entries()].map(([sport, t]) => (
                          <li key={sport} className="flex items-center justify-between">
                            <span className="capitalize text-foreground/80">{sport}</span>
                            <span className="tabular-nums text-muted-foreground">
                              {formatNumber(t.objects)} obj · {formatBytes(t.bytes)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent admin actions</CardTitle>
            <CardDescription>Queued from the Cron jobs page; drained by the .NET host.</CardDescription>
          </CardHeader>
          <CardContent>
            {actions.isError ? (
              <ErrorState error={actions.error} />
            ) : actions.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : actions.data?.length ? (
              <ul className="space-y-2">
                {actions.data.map((a) => (
                  <li key={a.action_id} className="flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{a.action_type}</div>
                      <div className="truncate text-muted-foreground">{a.job_id ?? '—'}</div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <Badge variant={stateVariant(a.status)}>{a.status}</Badge>
                      <span className="text-[10px] text-muted-foreground">{formatRelative(a.requested_at)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-muted-foreground">No admin actions yet.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
